import type { ITaskBase, ScheduledTaskResponse } from "@/types/task";
import type { QueryClient, UseInfiniteQueryReturnType } from "@tanstack/vue-query";
import { get } from "lodash-es";

type TaskListResponse<T = ITaskBase> = { items: T[]; next?: string };

export const taskListLimit = ref(50);
export function useListTasks(
  _enabled?: globalThis.Ref<boolean> | boolean | globalThis.ComputedRef<boolean>
): UseInfiniteQueryReturnType<ITaskBase[], Error> {
  const enabled = computed(
    () =>
      !!currentSessionInformation.appAuthToken &&
      (_enabled === undefined || !!unref(_enabled))
  );
  const query = useInfiniteQuery({
    queryKey: ["tasks", { limit: taskListLimit }],
    queryFn: async ({ pageParam }: { pageParam?: string } = {}) => {
      const response = await useGetRequest<TaskListResponse>("tasks", {
        params: {
          nextQuery: pageParam,
          __limit: taskListLimit.value,
        },
      });
      return get(response, "data", null);
    },
    select: (data: Record<any, any>) => {
      return data?.pages?.map((p: Record<any, any>) => p.items)?.flat() || [];
    },
    initialPageParam: undefined,
    getNextPageParam: (res: Record<any, any>) => {
      if (!res?.next) return null;

      const query = res.next?.split("?") || [];

      const searchParams = new URLSearchParams(query.slice(1).join("?"));
      const nextQuery = searchParams.get("nextQuery");

      if (query[0] !== "tasks" || !nextQuery) {
        return null;
      }
      return nextQuery;
    },
    enabled,
    staleTime: 2 * MINUTE,
  });

  return query;
}

export function updateItemStatusInTaskListQueryCache(
  queryClient: QueryClient,
  taskId: string,
  status: Extract<keyof ITaskBase, "canceled" | "error" | "running" | "complete">
) {
  const queryKey = ["tasks", { limit: taskListLimit }];
  const data = queryClient.getQueryData<{
    pages: {
      items: ITaskBase[];
      next?: string;
    }[];
    pageParams: string[];
  }>(queryKey);

  if (!data?.pages?.length) {
    queryClient.invalidateQueries({ queryKey });
    return;
  }
  let wasUpdated = false;

  const newData = { ...data };

  newData.pages = newData.pages.map(page => ({
    ...page,
    items: page.items.map(item => {
      if (item.id === taskId) {
        wasUpdated = true;
        const newItem = { ...item };
        newItem.running = 0;
        newItem.complete = 0;
        newItem.canceled = 0;
        newItem.error = 0;
        newItem.queued = 0;
        newItem[status] = 1;
        return newItem;
      }
      return item;
    }),
  }));

  if (!wasUpdated) {
    queryClient.invalidateQueries({ queryKey });
  }

  queryClient.setQueryData(["tasks", { limit: taskListLimit }], newData);
}

export function useScheduledTasks(
  enabled?: globalThis.Ref<boolean> | boolean | globalThis.ComputedRef<boolean>
): UseInfiniteQueryReturnType<ScheduledTaskResponse[], Error> {
  const query = useInfiniteQuery({
    queryKey: ["scheduled-tasks"],
    queryFn: async ({ pageParam }: { pageParam?: string } = {}) => {
      const response = await useGetRequest<TaskListResponse<ScheduledTaskResponse>>(
        "/scheduled-tasks",
        {
          params: {
            nextQuery: pageParam,
            scheduleType: "task",
            __limit: taskListLimit.value,
          },
        }
      );
      return get(response, "data", null);
    },
    placeholderData: { pages: [{ items: [], next: "" }], pageParams: [] },
    initialPageParam: undefined,
    getNextPageParam: (res: Record<any, any>) => {
      if (!res?.next) return null;
      const query = res.next?.split("?") || [];

      const searchParams = new URLSearchParams(query.slice(1).join("?"));
      const nextQuery = searchParams.get("nextQuery");

      if (!nextQuery) {
        return null;
      }
      return nextQuery;
    },
    select: (data: Record<any, any>) => {
      return data?.pages?.map((p: Record<any, any>) => p.items)?.flat() || [];
    },
    enabled,
    refetchOnMount: "always",
    staleTime: 1 * MINUTE,
  });

  return query;
}
